// Filtering a list as one types (02-building-blocks.md, "Lists and scrolling"): names and query in
// Unicode NFKC form, lower case, without white space; a name matches when it starts with the query
// (or contains it, where the area says so). Long lists are searched in slices of about 8 ms.

export const normalise = (s: string) => s.normalize('NFKC').toLowerCase().replace(/\s+/g, '');

export function matches(name: string, query: string, contains = false): boolean {
  const n = normalise(name);
  const q = normalise(query);
  return contains ? n.includes(q) : n.startsWith(q);
}

const SLICE_MS = 8;

/**
 * Tests `items` in slices so typing never stalls; `found` gets each slice's matches as they come.
 * Returns a function that stops the search (call it when the query changes).
 */
export function filterInSlices<T>(items: readonly T[], test: (item: T) => boolean, found: (batch: T[], done: boolean) => void): () => void {
  let i = 0;
  let stopped = false;
  const step = () => {
    if (stopped) return;
    const until = performance.now() + SLICE_MS;
    const batch: T[] = [];
    while (i < items.length && performance.now() < until) {
      const item = items[i++];
      if (test(item)) batch.push(item);
    }
    found(batch, i >= items.length);
    if (i < items.length) setTimeout(step, 0);
  };
  step();
  return () => void (stopped = true);
}

const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);
/** A word break: the start or end, a character that isn't a letter or digit, or an underscore. */
const breakAt = (s: string, i: number) => !isWordChar(s[i]);

interface Hit {
  length: number;
  at: number;
  startsWord: boolean;
  wholeWord: boolean;
}

function hit(name: string, q: string): Hit {
  const at = name.indexOf(q);
  const startsWord = at === 0 || breakAt(name, at - 1);
  return { length: name.length, at, startsWord, wholeWord: startsWord && breakAt(name, at + q.length) };
}

/**
 * Two matching names compared, each pair scored afresh (02, Lists): 1 point to the shorter, 2 to
 * the earlier match (the shorter at the same place), 4 for starting a word and 8 for a whole word
 * (to the shorter when both have it). Negative: `a` first.
 */
export function compareMatches(a: string, b: string, query: string): number {
  const [x, y, q] = [normalise(a), normalise(b), normalise(query)];
  const [ha, hb] = [hit(x, q), hit(y, q)];
  let pa = 0;
  let pb = 0;
  const toShorter = (points: number) => {
    if (ha.length < hb.length) pa += points;
    else if (hb.length < ha.length) pb += points;
  };
  const award = (has: (h: Hit) => boolean, points: number) => {
    if (has(ha) && has(hb)) toShorter(points);
    else if (has(ha)) pa += points;
    else if (has(hb)) pb += points;
  };
  toShorter(1);
  if (ha.at < hb.at) pa += 2;
  else if (hb.at < ha.at) pb += 2;
  else toShorter(2);
  award((h) => h.startsWord, 4);
  award((h) => h.wholeWord, 8);
  return pb - pa;
}

/** Matches before non-matches, ranked; equal points keep the list's own order. */
export function rank<T>(items: readonly T[], nameOf: (item: T) => string, query: string, contains = false): T[] {
  const found = items.filter((i) => matches(nameOf(i), query, contains));
  return found.sort((a, b) => compareMatches(nameOf(a), nameOf(b), query));
}
